import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { LoadErrorBox } from "../../components/common/LoadErrorBox";
import { Card } from "../../components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Cell,
} from "recharts";

const Stat = ({ label, value, testId, sub }) => (
  <div className="bg-surface border border-border rounded-xl shadow-sm p-6">
    <div className="text-[11px] uppercase tracking-widest text-text-muted mb-2">{label}</div>
    <div className="text-3xl tracking-tight font-medium text-text-primary" data-testid={testId}>{value}</div>
    {sub && <div className="text-xs text-text-muted mt-1">{sub}</div>}
  </div>
);

const COLORS = ["#3B82F6", "#60A5FA", "#93C5FD", "#1D4ED8", "#1E40AF"];

export default function AdminAnalytics() {
  const [data, setData] = useState(null);

  const [failed, setFailed] = useState(false);
  const load = () => {
    setFailed(false);
    api.get("/admin/analytics").then(({ data }) => setData(data)).catch(() => setFailed(true));
  };
  useEffect(load, []);

  if (failed) return <LoadErrorBox what="le statistiche" onRetry={load} testId="analytics-load-error" />;
  if (!data) return <div className="text-sm text-text-muted">Caricamento…</div>;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl tracking-tight font-medium text-text-primary">Analytics</h1>
        <p className="text-sm text-text-secondary mt-1">Metriche di utilizzo e spesa.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <Stat label="Spesa questo mese" value={`€ ${data.month_total_eur.toFixed(2)}`} testId="analytics-month-spend" />
        <Stat label="Richieste mese" value={data.month_count} testId="analytics-month-count" />
        <Stat label="Cache hit rate" value={`${data.cache_hit_rate}%`} testId="analytics-cache-rate" />
        <Stat label="Richieste totali" value={data.total_requests} testId="analytics-total" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-surface border border-border rounded-xl shadow-sm p-6" data-testid="chart-by-user">
          <div className="mb-4">
            <h3 className="text-sm font-medium text-text-primary">Spesa per utente (mese)</h3>
            <p className="text-xs text-text-muted mt-0.5">Top 10</p>
          </div>
          {data.spend_by_user.length === 0 ? (
            <div className="text-sm text-text-muted py-12 text-center">Nessun dato disponibile</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.spend_by_user} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="user" tick={{ fontSize: 11, fill: "#64748B" }} interval={0} angle={-15} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }}
                  formatter={(v) => [`€ ${v.toFixed(2)}`, "Spesa"]} />
                <Bar dataKey="spend_eur" radius={[6, 6, 0, 0]}>
                  {data.spend_by_user.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="bg-surface border border-border rounded-xl shadow-sm p-6" data-testid="chart-by-service">
          <div className="mb-4">
            <h3 className="text-sm font-medium text-text-primary">Spesa per servizio (mese)</h3>
            <p className="text-xs text-text-muted mt-0.5">Top 10</p>
          </div>
          {data.spend_by_service.length === 0 ? (
            <div className="text-sm text-text-muted py-12 text-center">Nessun dato disponibile</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.spend_by_service} layout="vertical" margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#64748B" }} />
                <YAxis type="category" dataKey="service" tick={{ fontSize: 11, fill: "#64748B" }} width={140} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }}
                  formatter={(v) => [`€ ${v.toFixed(2)}`, "Spesa"]} />
                <Bar dataKey="spend_eur" radius={[0, 6, 6, 0]} fill="#3B82F6" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      <Card className="bg-surface border border-border rounded-xl shadow-sm p-6">
        <h3 className="text-sm font-medium text-text-primary mb-4">Top servizi per richieste</h3>
        <div className="space-y-2.5">
          {data.spend_by_service.slice(0, 5).map((s, i) => (
            <div key={s.service} data-testid={`top-svc-${i}`} className="flex items-center justify-between py-2 border-b border-border last:border-0">
              <div className="text-sm text-text-primary">{s.service}</div>
              <div className="text-sm text-text-secondary">{s.count} richieste · € {s.spend_eur.toFixed(2)}</div>
            </div>
          ))}
          {data.spend_by_service.length === 0 && (
            <div className="text-sm text-text-muted py-6 text-center">Nessun dato</div>
          )}
        </div>
      </Card>
    </div>
  );
}
